import { type HttpResponseBase } from '@angular/common/http';
import { inject, Injectable, isDevMode } from '@angular/core';
import { type ErrorLike } from '@apollo/client';
import { CombinedGraphQLErrors } from '@apollo/client/errors';
import {
  BugsnagClient,
  type BugsnagErrorClass,
  isServerDownErrorMessage,
  showServerDownModal,
} from '@reconcept-platform/shared/util';
import { type GraphQLFormattedError } from 'graphql';
import { partition } from 'lodash-es';
import { errorIsTimeout } from './apollo-graphql.utils';

@Injectable({
  providedIn: 'root',
})
export class ApolloGraphqlErrorService {
  private bugsnagClient = inject(BugsnagClient);

  processApolloNetworkError(error: GraphQLFormattedError): void {
    if (isDevMode()) {
      console.warn('DEV - CombinedGraphQLErrors:', error);
    }

    // set metaData for bugsnag
    this.notifyBugsnag('ApolloError', error, new Error(error.message), 'ApolloError');

    showServerDownModal.set(
      // networkError is a different class because of Angular, while apollo client is React targeted
      isServerDownErrorMessage(error.message) && (error as unknown as HttpResponseBase)?.status !== 500,
    );

    // rethrow error
    throw error;
  }

  processQuery(errors: readonly GraphQLFormattedError[], operationName: string, inputVariables?: any): void {
    // if there are no errors, do nothing
    if (!errors?.length) {
      return;
    }

    this.sendErrorsToBugsnag(errors, 'GraphQlQuery', operationName, inputVariables);
  }

  processMutation(errors: readonly GraphQLFormattedError[], mutationName: string, inputVariables?: any): void {
    // if there are no errors, do nothing
    if (!errors?.length) {
      return;
    }

    this.sendErrorsToBugsnag(errors, 'GraphQlMutation', mutationName, inputVariables);
  }

  processSubscription(error: ErrorLike, operationName: string, inputVariables?: any): void {
    if (CombinedGraphQLErrors.is(error)) {
      this.sendErrorsToBugsnag(error.errors, 'GraphQlQuery', operationName, inputVariables);
      return;
    }

    if (isDevMode()) {
      console.warn('DEV - Subscription error:', operationName, error);
    }

    this.notifyBugsnag(
      'ApolloError',
      { error, inputVariables },
      new Error(`${error.message} on ${operationName}`),
      'ApolloError',
    );
  }

  /**
   * For some batch mutations, we return the error, and let the call site handle it
   *
   * @param result
   * @param mutationName
   * @param inputVariables
   */
  gracefulFailMutation(result: any, mutationName: string, inputVariables?: any) {
    const errors = this.extractErrors(result);

    // if there are no errors, do nothing
    if (!errors?.length) {
      return undefined;
    }

    // top-level errors already went through the ErrorLink, only the errors per mutation are new
    this.sendErrorsToBugsnag(
      errors.filter((error) => 'mutation' in error),
      'GraphQlMutation',
      mutationName,
      inputVariables,
    );

    return errors;
  }

  private sendErrorsToBugsnag(
    errors: readonly GraphQLFormattedError[],
    type: 'GraphQlQuery' | 'GraphQlMutation',
    operationName: string,
    inputVariables?: any,
  ) {
    // if there are no errors, do nothing
    if (!errors?.length) {
      return null; // is fine because it does not expect a return value elsewhere
    }

    if (isDevMode()) {
      console.warn(`DEV - ${type} errors:`, operationName, errors);
    }

    const [timeouts, others] = partition(errors, (error) => errorIsTimeout(error.message));

    // combine timeouts into 1 error
    if (timeouts.length) {
      this.notifyBugsnag(
        type,
        { errors: timeouts.map((timeout) => timeout.message), inputVariables },
        new Error(`Timeout on ${operationName}`),
        'Timeout',
      );
    }

    others.forEach((error) => {
      this.notifyBugsnag(
        type,
        // all errors of the response, so an event shows which errors arrived together
        { mutationErrors: error, responseErrors: errors, inputVariables },
        new Error(`${error.message} on ${operationName}`),
        type,
      );
    });

    return errors;
  }

  private extractErrors(result: any) {
    // copy, so the mutation errors below aren't pushed into Apollo's result
    const errors: GraphQLFormattedError[] = [...(result.errors ?? [])];

    // search the response for a mutation object with errors
    for (const mutationKey in result.data) {
      const graphlQLResponse = result.data[mutationKey];
      if (graphlQLResponse?.errors?.length) {
        // graphlQLResponse optional because it can be null in (some) error cases e.g. change user email with wrong password
        errors.push(
          ...graphlQLResponse.errors.map((error: any) => ({
            mutation: mutationKey,
            message: error.message,
            path: error.path,
          })),
        );
      }
    }
    return errors;
  }

  // todo: errorclass must be a defined list of strings, since these are also defined in the bugsnag project settings
  private notifyBugsnag(bugsnagTab: string, metaData: any, error: Error, errorClass: BugsnagErrorClass) {
    this.bugsnagClient.notify(error, { [bugsnagTab]: metaData }, errorClass);
  }
}
